import { memo, useMemo } from 'react';
import styled from 'styled-components';
import { useCallbackRef, usePortal } from '../../../hooks';
import { EMPTY_ARRAY } from '../../../utils';
import { Box, HStack, VStack } from '../../Core';
import { Tab, TabAppearance, TabList, TabSize, useAddTabPopover } from '../../Tabs';
import type { useEntityAdmin } from '../useEntityAdmin';
import { useEntityAdminTabsContext } from '../wrappers/EntityAdminTabsWrapper';
import { useEntityAdminHeaderLayout } from './EntityAdminHeaderLayoutContext';

export const EntityAdminTabsAndFilters = memo(
  ({ filtersPortalId }: { filtersPortalId: ReturnType<typeof useEntityAdmin>['filtersPortalId'] }) => {
    const { setPortalRef: filtersContainerRef } = usePortal(filtersPortalId);
    const tabsContext = useEntityAdminTabsContext();
    const { isPanelLayout } = useEntityAdminHeaderLayout();

    //Build the tab type options by filtering out tabs that don't have a tabType or label
    const tabTypeOptions = useMemo(() => {
      return (
        tabsContext?.defaultInitialItems
          .filter(tab => tab.tabType && tab.label)
          .map(tab => {
            return { label: tab.label!, tabType: tab.tabType! };
          }) ?? EMPTY_ARRAY
      );
    }, [tabsContext?.defaultInitialItems]);

    const handleSelectTabType = useCallbackRef((item: { label: string; tabType: string }) =>
      tabsContext?.onAdd({ label: item.label, tabType: item.tabType })
    );

    const { renderAddTab } = useAddTabPopover<{ label: string; tabType: string }>({
      options: tabTypeOptions,
      getLabel: item => item.label,
      onSelection: handleSelectTabType,
      appearance: TabAppearance.Filled,
      size: TabSize.Small,
    });

    const filters = <FiltersContainerBox ref={filtersContainerRef} />;

    return (
      <VStack w="100%" flex="0 0 auto">
        {tabsContext != null ? (
          <TabList
            w="100%"
            overflow="hidden"
            background="backgroundContent"
            px={isPanelLayout ? undefined : 'spacing12'}
            renderAddTab={tabsContext.defaultInitialItems.length > 1 ? renderAddTab : undefined}
            rightItems={filters}
          >
            {tabsContext.tabItems.map(item => (
              <Tab key={item.id} {...item} />
            ))}
          </TabList>
        ) : (
          <HStack
            w="100%"
            h="40px"
            px={isPanelLayout ? 'spacing12' : 'spacing24'}
            background="backgroundContent"
            justifyContent="flex-end"
          >
            {filters}
          </HStack>
        )}
      </VStack>
    );
  }
);

const FiltersContainerBox = styled(Box)`
  @media (max-width: 576px) {
    display: none;
  }
`;
